import { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import CookieSessionAuthHandler from './CookieSessionAuth';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../services/firebase';

interface QueryTokenAuthHandlerProps {
    children: React.ReactNode;
}

/**
 * Component that handles query token authentication
 * Processes auth query params and creates session cookies
 */
const QueryTokenAuthHandler: React.FC<QueryTokenAuthHandlerProps> = ({ children }) => {
    const [isProcessingCustomToken, setIsProcessingCustomToken] = useState(true);
    const [hasProcessedQueryToken, setHasProcessedQueryToken] = useState(false);

    // Handle auth query params 
    useEffect(() => {
        const processUrlToken = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const customToken = urlParams.get('token');
            
            if (customToken) {
                setIsProcessingCustomToken(true);

                try {
                    await signInWithCustomToken(auth, customToken);
                    setHasProcessedQueryToken(true);
                    //redirect to current url without the token query param
                    const url = new URL(window.location.href);
                    url.searchParams.delete('token');
                    window.location.href = url.toString();
                    
                } catch (error) {
                    console.error('URL token processing error:', error);
                } 
            }
            setIsProcessingCustomToken(false);
            
        };

        processUrlToken();
    }, []);

    // Show loading state while signInWithCustomToken
    if (isProcessingCustomToken) {
        return (
            <Box 
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    gap: 2,
                }}
            >
                <CircularProgress />
                <Typography variant="body1" color="text.secondary">
                    Authenticating...
                </Typography>
            </Box>
        );
    }

    // Render children normally with CookieSessionAuth (only if no URL token was processed)
    return (
        <>
            {!hasProcessedQueryToken && (
                <CookieSessionAuthHandler />
            )}
            {children}
        </>
    );
};

export default QueryTokenAuthHandler;
